import { BoltWhiteIcon as Bolt } from "@/assets/icons";
import CheckCircleIcon from "@/assets/icons/Empty check circle.svg?react";
import InfoIcon from "@/assets/icons/Info outlined.svg?react";
import XCircleIcon from "@/assets/icons/X circle.svg?react";
import { useLoggedInUser } from "@/components/providers/ApiProvider/hooks";
import { useCheckoutControllerGetSubscriptionCheckoutSession } from "@/lib/lutely-api/gen/checkout/checkout";
import { saveRedirect, savePendingSubscription } from "@/lib/storage-utils";
import { Badge, Button, Flex, Text, Tooltip } from "@radix-ui/themes";
import { useNavigate } from "@tanstack/react-router";

export default function PlanCard({
  period,
  planData,
  isPromoExperience = false,
  onCheckoutStart,
  onCheckoutError,
}: {
  period: "yearly" | "monthly";
  isPromoExperience?: boolean;
  onCheckoutStart?: () => void;
  onCheckoutError?: () => void;
  planData: {
    title: string;
    description: string;
    originalMonthlyPrice?: string;
    monthlyPrice: string;
    monthlyYearlyPrice: string;
    yearlyPrice: string;
    discount?: string;
    featured?: boolean;
    freePlan?: boolean;
    subtitle: string;
    offerings: {
      type: string;
      title: string;
      bold?: string;
      helper?: string;
      isBeta?: boolean;
    }[];
    comingSoon?: {
      type: string;
      title: string;
    }[];
    monthlyIdentifier?: string;
    yearlyIdentifier?: string;
  };
}) {
  const navigate = useNavigate();
  const isFree = planData.freePlan;
  const {
    data: user,
    subscription,
    isLoading,
    isLabelSubscriber,
  } = useLoggedInUser();
  const identifier =
    period == "yearly" ? planData.yearlyIdentifier : planData.monthlyIdentifier;
  const isActive =
    (!isFree && subscription?.subscriptionIdentifier === identifier) ||
    !!(isFree && user);

  const { mutateAsync: getCheckoutSession } =
    useCheckoutControllerGetSubscriptionCheckoutSession();

  if (isLoading) {
    return null;
  }

  return (
    <Flex
      direction="column"
      data-testid={`plan-card-${planData.title.toLowerCase()}`}
      className={`w-full max-w-[400px] border rounded-[12px] items-center shadow-md transition-all hover:shadow-xl overflow-hidden relative ${planData.featured ? " bg-blue-100 border-blue-300 hover:border-accent-primary" : " bg-background-secondary border-border-tertiary hover:border-border-primary"}`}
    >
      <Flex
        className={`py-[60px] px-[20px] items-center bg-white w-full ${isFree ? "pb-[65px]" : "pb-[48px]"}`}
        direction="column"
      >
        <Text
          className={`text-[36px] font-bold font-figtree ${planData.featured ? "text-brand-primary" : "text-blue-900"}`}
        >
          {planData.title}
        </Text>
        <Text className="text-[#525966] mb-[16px]">{planData.description}</Text>
        {isPromoExperience && !isFree && planData.originalMonthlyPrice && (
          <Text className="text-[14px] text-[#525966] line-through leading-[0.1] font-semibold">
            ${parseFloat(planData.originalMonthlyPrice!).toFixed(2)}/ month
          </Text>
        )}
        <Flex className="items-center gap-[4px]">
          <Text className="text-[24px] font-bold text-blue-900">
            $
            {period == "yearly"
              ? planData.monthlyYearlyPrice
              : planData.monthlyPrice}
          </Text>
          {!isFree && (
            <Text className="text-[14px] text-[#525966]">/ month</Text>
          )}
        </Flex>
        {!isFree ? (
          period == "yearly" ? (
            <Text className="text-[12px] text-[#525966]">
              Billed ${planData.yearlyPrice} annually
            </Text>
          ) : (
            <Text className="text-[12px] text-[#525966]">Billed monthly</Text>
          )
        ) : null}
      </Flex>
      <Button
        data-testid={`plan-card-cta-${planData.title.toLowerCase()}`}
        onClick={async () => {
          const subscriptionIdentifier = identifier as
            | "CreatorMonthly"
            | "CreatorAnnual"
            | "LabelMonthly"
            | "LabelAnnual";

          if (!user) {
            savePendingSubscription(subscriptionIdentifier);

            const redirectUrl = isPromoExperience
              ? "/upgrade?promo=LIFETIME50"
              : "/upgrade";

            saveRedirect(redirectUrl);
            navigate({
              to: ".",
              search: (prev: Record<string, unknown>) => ({
                ...prev,
                auth: "signup" as const,
              }),
            });
            return;
          }

          onCheckoutStart?.();
          try {
            const result = await getCheckoutSession({
              subscriptionIdentifier,
              data: {
                discounts: isPromoExperience
                  ? [{ promotion_code: "LIFETIME50" }]
                  : undefined,
              },
            });

            window.location.href = result.url;
          } catch (error) {
            onCheckoutError?.();
            // TODO: Show error to user
            console.error("Error creating checkout session:", error);
          }
        }}
        size="3"
        disabled={isActive}
        loading={isLoading}
        color={planData.featured ? undefined : "gray"}
        radius="full"
        className={`mt-[-18px] border-[3px] w-[200px] z-[2] border-solid ${planData.featured ? "border-accent-tertiary" : "border-background-neutral-quarternary"}`}
      >
        {isActive
          ? "Current Plan"
          : isFree
            ? "Get started"
            : isLabelSubscriber
              ? "Switch to " + planData.title
              : "Upgrade to " + planData.title}
      </Button>
      <Flex
        direction="column"
        className="mt-[-18px] w-full p-[56px] pl-[32px] pr-[32px] gap-[20px]"
      >
        <Text className="text-[#525966] text-[12px] font-semibold">
          {planData.subtitle}
        </Text>
        {planData.offerings.map((offering, i) => (
          <Flex key={i} className="items-center justify-start gap-[4px]">
            {offering.type === "check" ? (
              <CheckCircleIcon
                width="18px"
                height="18px"
                className="text-brand-primary"
              />
            ) : (
              <XCircleIcon
                width="18px"
                height="18px"
                className="fill-error-bold"
              />
            )}
            <Text className="text-[14px] text-blue-900">
              {offering.bold ? <b>{offering.bold} </b> : null}
              {offering.title}
            </Text>
            {offering?.helper && (
              <Tooltip
                content={
                  <Flex direction="column" className="gap-[10px] text-[14px]">
                    <Text>
                      Track Unlocks allow you to instantly download and
                      permanently own all of a track's assets, including the
                      master, stems, instrumental, lyrics, and CleanCuts.
                    </Text>
                    <Text>Unused unlocks do not roll over.</Text>
                  </Flex>
                }
              >
                <InfoIcon width="18px" height="18px" />
              </Tooltip>
            )}
            {offering.isBeta && (
              <div className="flex h-6 items-center gap-[4px] rounded-[4px] border border-[#C7D8F9] bg-[#EEF4FF] shadow-[0_5px_8px_0_rgba(5,12,43,0.14)] transition-colors px-2 py-0.5">
                <div className="flex items-center justify-center text-[#3042E2] [&>svg]:!fill-[#3042E2] [&>svg>path]:!fill-[#3042E2] [&>svg>*]:!fill-[#3042E2]">
                  <Bolt className="w-3 h-3" />
                </div>
                <span className="font-open-sans text-sm font-semibold leading-[140%] text-[#3042E2]">
                  Beta (Pro)
                </span>
              </div>
            )}
          </Flex>
        ))}
        {planData.comingSoon?.length && (
          <>
            <Text className="text-[#525966] text-[12px] font-semibold">
              Coming Soon:
            </Text>
            {planData.comingSoon.map((offering, i) => (
              <Flex key={i} className="items-center justify-center gap-[4px]">
                {offering.type === "check" ? (
                  <CheckCircleIcon
                    width="18px"
                    height="18px"
                    className="text-foreground-quarternary"
                  />
                ) : (
                  <XCircleIcon
                    width="18px"
                    height="18px"
                    className="text-foreground-quarternary"
                  />
                )}

                <Text className="flex-1 text-[14px] text-text-primary">
                  {offering.title}
                </Text>
              </Flex>
            ))}
          </>
        )}
      </Flex>
      {((planData?.discount && period === "yearly") ||
        (isPromoExperience && planData?.discount)) && (
        <Flex className="absolute top-0 right-0">
          <Badge className="p-[8px] pl-[16px] pr-[16px] bg-teal-100 text-brand-primary text-[14px] rounded-[0] rounded-bl-[10px]">
            {planData.discount}
          </Badge>
        </Flex>
      )}
    </Flex>
  );
}
